import React, { useState } from 'react';
import { downloadExport, readExportError } from 'state';
import { EXPORT_FORMATS, EXPORT_DEFAULT_FORMAT } from '../../fields.constants';
import { formatCount, gpxWarningText, downloadedSummary } from '../../fields.utils';
import {
  Card,
  Title,
  Label,
  Select,
  Warning,
  PreviewButton,
  DownloadButton,
  Sent,
  ErrorNote
} from './DownloadPanel.styles';

export const DownloadPanel = ({
  fieldName,
  surveyId,
  surveyTotal,
  draft,
  isPreview,
  onTogglePreview,
  onDownloaded,
  children
}) => {
  const [format, setFormat] = useState(EXPORT_DEFAULT_FORMAT);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);

  const selected = draft?.selected ?? 0;
  const isTooBigForGpx = format === 'gpx' && Boolean(draft?.gpx.overLimit);

  const download = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      const filename = await downloadExport({
        surveyId,
        format,
        capPerAcre: draft.capPerAcre
      });
      setSent({
        filename,
        summary: downloadedSummary({
          selected: draft.selected,
          mustPick: draft.mustPick,
          mustPickCm: draft.mustPickCm,
          fieldName,
          surveyTotal
        })
      });
      onDownloaded();
    } catch (downloadError) {
      setError(await readExportError(downloadError));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card data-testid='download-panel'>
      <Title>Download for the stone picker</Title>
      <Label htmlFor='export-format'>Format</Label>
      <Select
        id='export-format'
        value={format}
        onChange={event => setFormat(event.target.value)}
      >
        {EXPORT_FORMATS.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </Select>
      {isTooBigForGpx && (
        <Warning data-testid='gpx-warning'>
          <b>Too many for a GPX file</b>
          {gpxWarningText({
            selected,
            maxWaypoints: draft.gpx.maxWaypoints,
            maxCapPerAcre: draft.gpx.maxCapPerAcre
          })}
        </Warning>
      )}
      <PreviewButton
        type='button'
        disabled={!isPreview && selected === 0}
        onClick={onTogglePreview}
      >
        {isPreview ? 'Back to the full field' : 'Check the pick list on the map'}
      </PreviewButton>
      <DownloadButton
        type='button'
        disabled={selected === 0 || isTooBigForGpx || draft?.isStale || isDownloading}
        onClick={download}
      >
        {isDownloading ? 'Preparing…' : `Download ${formatCount(selected)} stones`}
      </DownloadButton>
      {error && <ErrorNote role='alert'>{error}</ErrorNote>}
      {sent && (
        <Sent data-testid='download-sent'>
          <b>{sent.filename} downloaded</b>
          {sent.summary}
        </Sent>
      )}
      {children}
    </Card>
  );
};
